import { Close } from "@mui/icons-material";
import { Divider, IconButton, Modal, Stack, Typography } from "@mui/material";
import { FormEvent, useCallback, useState } from "react";
import { ProductPrice } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton";
import Input from "src/components/FormInput";
import { desktopSx } from "src/utils/styles/modalStyles";
import { useInputs } from "./useInputs";
import { useCreateNewTitrationMutation } from "src/api/services/titrationApi";
import { useSentryLogger } from "src/hooks/useSentryLogger";

interface Props {
  open: boolean;
  onClose: () => void;
  price: ProductPrice;
}

const AddTitrationModal = ({ onClose, open, price }: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { inputs, setValues, values } = useInputs();
  const [createTitration] = useCreateNewTitrationMutation();
  const errLog = useSentryLogger("error");

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);

      try {
        await createTitration({
          duration_in_months: price.number_of_months,
          product_id: price.product_id,
          product_category_id: price.product_category_id,
          price_id: price.id,
          ...values,
        }).unwrap();

        setValues({
          dose_unit: "",
          tier: 0,
          total_dose: 0,
          max_dose: null,
          min_dose: null,
          slug: "",
        });
        localStorage.removeItem("open-titration");
        onClose();
      } catch (error) {
        errLog(`Could not create titration for price ${price.id}`, error);
      } finally {
        setLoading(false);
      }
    },
    [
      createTitration,
      errLog,
      onClose,
      price.id,
      price.number_of_months,
      price.product_category_id,
      price.product_id,
      setValues,
      values,
    ],
  );

  const handleClose = useCallback(() => {
    localStorage.removeItem("open-titration");
    onClose();
  }, [onClose]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Stack sx={desktopSx}>
        <Stack gap={3}>
          <Stack gap={1}>
            <Stack
              direction={"row"}
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h5" component="h5" fontWeight={700}>
                Add titration
              </Typography>
              <IconButton onClick={handleClose} sx={{ p: 0 }}>
                <Close fontSize="medium" />
              </IconButton>
            </Stack>
            <Divider sx={{ backgroundColor: "#000000" }} />
          </Stack>
          <Stack component={"form"} onSubmit={handleSubmit} gap={2}>
            {inputs.map((input) => (
              <Input key={input.label} current={input} />
            ))}
            <LoadingButton loading={loading} type="submit">
              Create
            </LoadingButton>
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default AddTitrationModal;
